/**
 * As visitas deste avaliador.
 *
 * Ordenadas pela data marcada, com as de hoje em destaque: o avaliador abre
 * isto de manhã para saber onde tem de estar, não para navegar num arquivo.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, MapPin, CalendarClock, ClipboardList, User, Phone, Hand } from 'lucide-react';
import api from '../../utils/api';

const dia = (v) => (v ? new Date(v).toLocaleDateString('pt-PT', { weekday: 'short', day: '2-digit', month: '2-digit' }) : 'Sem data');
const hora = (v) => (v ? new Date(v).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' }) : '');

const ehHoje = (v) => {
  if (!v) return false;
  const d = new Date(v); const h = new Date();
  return d.toDateString() === h.toDateString();
};

const SurveyHome = () => {
  const navigate = useNavigate();
  const [lista, setLista] = useState(null);
  const [ofertas, setOfertas] = useState([]);
  const [aAceitar, setAAceitar] = useState(null);   // { oferta, quando }
  const [busy, setBusy] = useState(false);
  const [erro, setErro] = useState('');

  const carregar = useCallback(() => {
    api.get('/projects')
      .then((r) => setLista(r.data?.projectos || []))
      .catch(() => setLista([]));
    api.get('/projects/visitas/ofertas')
      .then((r) => setOfertas(r.data || []))
      .catch(() => setOfertas([]));
  }, []);

  const aceitar = async () => {
    setBusy(true); setErro('');
    try {
      await api.post(`/projects/visitas/ofertas/${aAceitar.oferta.survey_id}/aceitar`,
        { agendado_para: new Date(aAceitar.quando).toISOString() });
      setAAceitar(null);
      carregar();
    } catch (e) {
      // 409 e o caso normal, nao a excepcao: outro chegou primeiro. A
      // mensagem tem de o dizer sem parecer avaria.
      setErro(e.response?.data?.message || 'Nao foi possivel aceitar.');
      carregar();
    } finally { setBusy(false); }
  };
  useEffect(() => { carregar(); }, [carregar]);

  if (lista === null) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-600" /></div>;
  }

  return (
    <div className="space-y-4">
      {/* As ofertas vêm primeiro, e em destaque: são a única coisa neste ecrã
          com prazo. Fica com a visita quem aceitar primeiro, por isso deixá-las
          a meio da lista era o mesmo que não as mostrar. */}
      {ofertas.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-sm font-bold text-amber-400 flex items-center gap-1.5">
            <Hand className="w-4 h-4" />
            {ofertas.length === 1 ? 'Uma visita disponível' : `${ofertas.length} visitas disponíveis`}
          </h2>
          <p className="text-xs text-slate-400">
            Fica com o trabalho quem aceitar primeiro. Ao aceitar, marca o dia e a hora.
          </p>
          {ofertas.map((o) => (
            <div key={o.oferta_id} className="p-4 rounded-xl border border-amber-500/40 bg-amber-500/5">
              <div className="font-semibold">{o.titulo || o.categoria}</div>
              <div className="text-xs text-slate-500 font-mono mt-0.5">
                {o.codigo} · {o.categoria}
                {o.ambito === 'NOVA' ? ' · instalação nova' : o.ambito === 'EXISTENTE' ? ' · no existente' : ''}
              </div>
              <div className="flex items-center gap-3 mt-2 text-xs text-slate-400 flex-wrap">
                {o.zona && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {o.zona}</span>}
                {o.area_m2 && <span>{Number(o.area_m2).toLocaleString('pt-PT')} m²</span>}
                {/* A ronda 2 diz que já ninguém da zona quis — é informação
                    honesta para quem vai ter de atravessar a cidade. */}
                {o.ronda === 2 && <span className="text-amber-400">fora da sua zona</span>}
              </div>
              <button onClick={() => { setAAceitar({ oferta: o, quando: '' }); setErro(''); }}
                className="w-full mt-3 min-h-[46px] rounded-lg bg-amber-600 text-white font-semibold">
                Aceitar esta visita
              </button>
            </div>
          ))}
        </section>
      )}

      {aAceitar && (
        <div className="fixed inset-0 z-50 bg-black/70 p-4 overflow-y-auto"
          role="dialog" aria-modal="true" aria-label="Aceitar visita">
          <div className="mx-auto w-full max-w-md rounded-2xl border border-slate-700 bg-slate-900 p-5 my-8 space-y-3">
            <h2 className="font-bold">{aAceitar.oferta.titulo || aAceitar.oferta.categoria}</h2>
            <p className="text-sm text-slate-400">
              {aAceitar.oferta.localizacao}
            </p>
            <label className="block text-sm">
              <span className="block text-xs text-slate-500 mb-1">Quando vai ao local</span>
              <input type="datetime-local" value={aAceitar.quando}
                onChange={(e) => setAAceitar((a) => ({ ...a, quando: e.target.value }))}
                className="w-full px-3 min-h-[46px] rounded-lg bg-slate-950 border border-slate-700 text-slate-100" />
            </label>
            {erro && <p className="text-sm text-red-400">{erro}</p>}
            <div className="flex gap-2">
              <button onClick={() => setAAceitar(null)}
                className="flex-1 min-h-[46px] rounded-lg bg-slate-800 text-slate-300">Voltar</button>
              <button onClick={aceitar} disabled={busy || !aAceitar.quando}
                className="flex-1 min-h-[46px] rounded-lg bg-amber-600 text-white font-semibold disabled:opacity-40">
                {busy ? 'A aceitar…' : 'Aceitar'}
              </button>
            </div>
          </div>
        </div>
      )}

      <h1 className="text-xl font-bold">As minhas visitas</h1>

      {lista.length === 0 ? (
        <div className="text-center py-16 text-slate-500">
          <ClipboardList className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="font-medium text-slate-400">Nenhuma visita atribuída</p>
          <p className="text-sm mt-1">Quando houver uma visita na sua zona, aparece aqui para aceitar.</p>
        </div>
      ) : (
        <ul className="space-y-2">
          {lista.map((v) => {
            const hoje = ehHoje(v.agendado_para);
            return (
              <li key={v.survey_id || v.project_id}>
                <button onClick={() => navigate(`/admin/visitas/${v.project_id}`)}
                  className={`w-full text-left p-4 rounded-t-xl border
                    ${hoje ? 'border-cyan-500/50 bg-cyan-500/5' : 'border-slate-800 bg-slate-900'}`}>
                  <div className="flex items-start gap-3 flex-wrap">
                    <div className="flex-1 min-w-[160px]">
                      <div className="font-semibold">{v.titulo || v.categoria}</div>
                      <div className="text-xs text-slate-500 font-mono mt-0.5">{v.codigo}</div>
                    </div>
                    <span className={`px-2 py-1 rounded-md text-[11px] font-semibold
                      ${hoje ? 'bg-cyan-500/20 text-cyan-300' : 'bg-slate-800 text-slate-400'}`}>
                      {hoje ? 'HOJE' : dia(v.agendado_para)}
                    </span>
                  </div>

                  <div className="flex items-center gap-3 mt-2 text-xs text-slate-400 flex-wrap">
                    <span className="inline-flex items-center gap-1">
                      <CalendarClock className="w-3.5 h-3.5" />
                      {dia(v.agendado_para)} {hora(v.agendado_para)}
                    </span>
                    {v.localizacao && (
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5" /> {v.localizacao}
                      </span>
                    )}
                  </div>

                </button>

                {/* Por quem perguntar à porta, e para quem ligar se ninguém
                    abrir. Sem isto, o técnico tinha a morada e mais nada.
                    Fora do botão do cartão, para o número poder ser marcado. */}
                {(v.cliente_nome || v.cliente_telefone) && (
                  <div className={`flex items-center gap-2 px-4 py-2 -mt-px rounded-b-xl border border-t-0
                    ${hoje ? 'border-cyan-500/50 bg-cyan-500/5' : 'border-slate-800 bg-slate-900'}`}>
                    {v.cliente_foto
                      ? <img src={v.cliente_foto} alt="" className="w-7 h-7 rounded-full object-cover" />
                      : <User className="w-4 h-4 text-slate-500" />}
                    <span className="text-xs text-slate-300">{v.cliente_nome}</span>
                    {v.cliente_telefone && (
                      <a href={`tel:${v.cliente_telefone}`}
                        className="ml-auto inline-flex items-center gap-1 text-xs text-cyan-400 font-medium min-h-[44px]">
                        <Phone className="w-3.5 h-3.5" /> {v.cliente_telefone}
                      </a>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default SurveyHome;
